import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";

export interface Profile {
  id: string;
  username: string;
  full_name: string;
  avatar_url: string | null;
  banner_url: string | null;
  bio: string | null;
  skills: string[];
  interests: string[];
  updated_at: string;
  is_pro: boolean;
}

const formatUsername = (username: string) => {
  return username.toLowerCase().replace(/\s+/g, '_');
};

export const useProfile = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error("No user");

      console.log('Fetching profile for user:', user.id);

      try {
        // Get existing profile
        const { data: existingProfile, error: fetchError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .single();

        if (fetchError) {
          console.error('Error fetching profile:', fetchError);
          throw fetchError;
        }

        console.log('Fetched profile:', existingProfile);

        // If profile exists, normalize and return it
        if (existingProfile) {
          const normalizedProfile = {
            ...existingProfile,
            is_pro: existingProfile.is_pro === true,
            skills: existingProfile.skills || [],
            interests: existingProfile.interests || [],
          };
          return normalizedProfile;
        }

        // If no profile exists, create one
        console.log('Creating new profile for user:', user.id);
        const newProfile = {
          id: user.id,
          username: formatUsername(user.email?.split('@')[0] || 'user'),
          full_name: user.email?.split('@')[0] || 'User',
          avatar_url: null,
          banner_url: null,
          bio: null,
          skills: [],
          interests: [],
          is_pro: false,
          updated_at: new Date().toISOString(),
        };

        const { data: insertedProfile, error: insertError } = await supabase
          .from("profiles")
          .insert([newProfile])
          .select()
          .single();

        if (insertError) {
          console.error('Error creating profile:', insertError);
          throw insertError;
        }

        console.log('Created new profile:', insertedProfile);

        return {
          ...insertedProfile,
          is_pro: false,
          skills: [],
          interests: [],
        };

      } catch (error) {
        console.error('Profile operation failed:', error);
        throw error;
      }
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes (previously cacheTime)
  });

  // Debug logging
  useEffect(() => {
    if (profile) {
      console.log('Current profile state:', {
        id: profile.id,
        username: profile.username,
        is_pro: profile.is_pro,
      });
    }
  }, [profile]);

  const updateProfile = useMutation({
    mutationFn: async (updates: Partial<Profile>) => {
      if (!user?.id) throw new Error("No user");

      console.log('Updating profile with:', updates);

      const formattedUpdates = {
        ...updates,
        ...(updates.username && { username: formatUsername(updates.username) })
      };

      const { error } = await supabase
        .from("profiles")
        .update(formattedUpdates)
        .eq("id", user.id);

      if (error) {
        console.error('Error updating profile:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    profile,
    isLoading,
    updateProfile,
  };
};
